// FIX-CCS-G-GPS unit G4 — the one location engine for the whole app (GPS-3 S2 §2.2). A thin adapter around the pure
// reducer in ./engine.ts: it owns the single navigator.geolocation watch, the Permissions API listener, the
// visibility listener and a 1 s tick, and turns reducer effects into browser calls. Nothing else in the app may call
// navigator.geolocation for photo evidence (the old second lookup and maximumAge: 60000 go in G5).
//
// Usage: engine.demand(taskId) when a photo task opens (camera sheet), engine.release(taskId) when it closes,
// engine.shutter(photoId) at the pick — resolves with the R2 outcome (a bundle, verified or not, or no fix);
// engine.setPolicy(...) with get_gps_guarantee_policy's freshness_s / stale_max_s;
// engine.subscribe(fn) for the status the UI shows. The bundle's bundle_sha256 is added here (WebCrypto, async).

import { canonicalJson } from './canonicalJson';
import {
  DEFAULT_PARAMS, initialState, reduce, type BundleCore, type Emit, type Event, type NoFixReason, type Params, type Perm, type State,
} from './engine';

export type LocationBundle = BundleCore & { bundle_sha256: string };
export type ShutterOutcome =
  | { kind: 'bundle'; bundle: LocationBundle; verified: boolean }
  | { kind: 'no_fix'; reason: NoFixReason; status: State['status'] };

export interface GeoLike {
  watchPosition(ok: (p: GeolocationPosition) => void, err: (e: GeolocationPositionError) => void, o: PositionOptions): number;
  clearWatch(id: number): void;
}
export interface EngineEnv {
  geo: GeoLike | null;
  now: () => number;
  permissions: { query(d: { name: 'geolocation' }): Promise<PermissionStatus> } | null;
  doc: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'> | null;
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (h: unknown) => void;
  digest: (text: string) => Promise<string>;
}

async function sha256Hex(text: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(d), (x) => x.toString(16).padStart(2, '0')).join('');
}

function browserEnv(): EngineEnv {
  const hasWindow = typeof window !== 'undefined';
  const nav = hasWindow ? window.navigator : undefined;
  return {
    geo: nav && 'geolocation' in nav ? nav.geolocation : null,
    now: () => Date.now(),
    permissions: nav && nav.permissions ? (nav.permissions as EngineEnv['permissions']) : null,
    doc: typeof document !== 'undefined' ? document : null,
    setInterval: (fn, ms) => (hasWindow ? window.setInterval(fn, ms) : null),
    clearInterval: (h) => { if (hasWindow && typeof h === 'number') window.clearInterval(h); },
    digest: sha256Hex,
  };
}

export class LocationEngine {
  private state: State;
  private watchId: number | null = null;
  private tickHandle: unknown = null;
  private listeners = new Set<(s: State) => void>();
  private pending = new Map<string, (o: ShutterOutcome) => void>();
  private started = false;

  constructor(private env: EngineEnv, params: Params = DEFAULT_PARAMS) {
    this.state = initialState(params, 'unknown', env.now());
  }

  getState(): State { return this.state; }

  subscribe(fn: (s: State) => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  /** A photo task opened (camera sheet, plot capture). Starts the engine on first use. */
  demand(taskId: string): void {
    this.boot();
    this.dispatch({ type: 'DEMAND', task_id: taskId, ts: this.env.now() });
  }
  release(taskId: string): void { this.dispatch({ type: 'RELEASE', task_id: taskId, ts: this.env.now() }); }
  /** The farmer tapped "use my location" — the browser may show its prompt now. */
  request(): void { this.dispatch({ type: 'REQUEST', ts: this.env.now() }); }

  /** The farmer tapped the shutter button; the phone's camera app is about to open (records the anchor). */
  arm(photoId: string): void { this.dispatch({ type: 'ARM', photo_id: photoId, ts: this.env.now() }); }

  /** The pick. Decided at once (R2); resolves when the bundle hash is ready.
   *  B4: `captureTs` = when the photo was taken (best evidence first) for the tap-fix rule; `pickTs` = when the file
   *  arrived (so reading the capture time can never move the pick later). */
  shutter(photoId: string, opts: { captureTs?: number[]; pickTs?: number } = {}): Promise<ShutterOutcome> {
    return new Promise((resolve) => {
      this.pending.set(photoId, resolve);
      const ts = Number.isFinite(opts.pickTs) ? Math.min(opts.pickTs as number, this.env.now()) : this.env.now();
      this.dispatch({ type: 'SHUTTER', photo_id: photoId, ts, capture_ts: opts.captureTs });
    });
  }

  /** get_gps_guarantee_policy's own numbers (SD-O112: stale_max_s is read, never hard-coded). */
  setPolicy(p: { freshness_s: number | null; stale_max_s: number | null }): void {
    this.dispatch({ type: 'POLICY', freshness_s: p.freshness_s, stale_max_s: p.stale_max_s });
  }

  /** Photo ids picked whose outcome is not settled yet (the outbox keeps such an item back). */
  pendingIds(): ReadonlySet<string> { return new Set(this.pending.keys()); }

  private boot(): void {
    if (this.started) return;
    this.started = true;
    const { env } = this;
    if (!env.geo) { this.dispatch({ type: 'UNAVAILABLE', ts: env.now() }); return; }
    env.permissions?.query({ name: 'geolocation' }).then((st) => {
      this.dispatch({ type: 'PERM', state: st.state as Perm, ts: env.now() });
      st.onchange = () => this.dispatch({ type: 'PERM', state: st.state as Perm, ts: env.now() });
    }).catch(() => { /* no Permissions API for geolocation: stays 'unknown', the watch itself tells us */ });
    env.doc?.addEventListener('visibilitychange', () => {
      this.dispatch({ type: env.doc?.visibilityState === 'hidden' ? 'HIDDEN' : 'VISIBLE', ts: env.now() });
    });
    this.tickHandle = env.setInterval(() => this.dispatch({ type: 'TICK', ts: env.now() }), 1000);
  }

  private dispatch(ev: Event): void {
    const r = reduce(this.state, ev);
    this.state = r.state;
    for (const e of r.effects) {
      if (e.type === 'START_WATCH' && this.env.geo && this.watchId === null) {
        this.watchId = this.env.geo.watchPosition(
          (pos) => this.dispatch({
            type: 'FIX',
            fix: {
              lat: pos.coords.latitude, lng: pos.coords.longitude, acc_m: pos.coords.accuracy,
              fix_ts: pos.timestamp, recv_ts: this.env.now(),
              alt: pos.coords.altitude, alt_acc: pos.coords.altitudeAccuracy, speed: pos.coords.speed, heading: pos.coords.heading,
            },
          }),
          (err) => this.dispatch({ type: 'ERROR', code: err.code, ts: this.env.now() }),
          { ...e.options },
        );
      } else if (e.type === 'CLEAR_WATCH' && this.env.geo && this.watchId !== null) {
        this.env.geo.clearWatch(this.watchId);
        this.watchId = null;
      }
    }
    for (const e of r.emits) void this.settle(e);
    for (const fn of this.listeners) fn(this.state);
  }

  private async settle(e: Emit): Promise<void> {
    const resolve = this.pending.get(e.photo_id);
    if (!resolve) return;
    // the id stays pending until the outcome is delivered (the hash is async): the outbox keeps the item back till then
    let out: ShutterOutcome;
    if (e.type === 'BUNDLE') {
      const bundle_sha256 = await this.env.digest(canonicalJson(e.bundle));
      out = { kind: 'bundle', bundle: { ...e.bundle, bundle_sha256 }, verified: e.verified };
    } else out = { kind: 'no_fix', reason: e.reason, status: e.status };
    this.pending.delete(e.photo_id);
    resolve(out);
  }

  /** Tests only. */
  dispose(): void {
    if (this.watchId !== null) this.env.geo?.clearWatch(this.watchId);
    if (this.tickHandle !== null) this.env.clearInterval(this.tickHandle);
    this.listeners.clear();
  }
}

let singleton: LocationEngine | null = null;
/** The app's one engine. */
export function getLocationEngine(): LocationEngine {
  if (!singleton) singleton = new LocationEngine(browserEnv());
  return singleton;
}
